import { useMemo, useState } from "react";
import {
  Avatar,
  attachmentType,
  attachmentParts,
  fileNameFromUrl,
  shortTime,
  brandGradient,
  CloseIcon,
  StarIcon,
  BellIcon,
  BellOffIcon,
  BanIcon,
  FlagIcon,
  TrashIcon,
  SearchIcon,
  PaletteIcon,
  DocIcon,
  SparkIcon,
} from "./ui";

const REPORT_REASONS = [
  "Spam or scam",
  "Harassment or bullying",
  "Hate speech",
  "Inappropriate content",
  "Impersonation",
  "Something else",
];

export const WALLPAPERS = [
  { key: "aura", label: "Aura", css: null }, // premium default: brand glow + soft dots
  { key: "plain", label: "Plain", css: "#ffffff" },
  { key: "mist", label: "Mist", css: "linear-gradient(180deg,#f8fafc 0%,#ffffff 100%)" },
  { key: "brand", label: "Brand", css: null }, // resolved from `primary`
  { key: "dots", label: "Dots", css: null },
  { key: "dusk", label: "Dusk", css: "linear-gradient(180deg,#eef2ff 0%,#faf5ff 100%)" },
  { key: "sand", label: "Sand", css: "linear-gradient(180deg,#fffbeb 0%,#ffffff 100%)" },
];

/** Resolve a wallpaper key into an inline style for the message canvas. */
export function wallpaperStyle(key, primary) {
  // Premium default: a soft brand glow up top + a faint dot texture over a cool
  // off-white — reads modern and expensive without competing with the messages.
  if (key === "aura")
    return {
      backgroundColor: "rgb(var(--c-surface-sunken))",
      backgroundImage: `radial-gradient(1100px 460px at 50% -12%, ${primary}16, transparent 62%), radial-gradient(760px 380px at 100% 8%, ${primary}0d, transparent 60%), radial-gradient(${primary}14 1px, transparent 1px)`,
      backgroundSize: "auto, auto, 22px 22px",
      backgroundAttachment: "local, local, local",
    };
  if (key === "brand") return { background: `linear-gradient(180deg, ${primary}0f 0%, rgb(var(--c-surface)) 60%)` };
  if (key === "dots")
    return {
      backgroundColor: "rgb(var(--c-surface-sunken))",
      backgroundImage: `radial-gradient(${primary}26 1px, transparent 1px)`,
      backgroundSize: "18px 18px",
    };
  const w = WALLPAPERS.find((x) => x.key === key);
  return { background: w?.css || "linear-gradient(180deg,#f8fafc 0%,#ffffff 100%)" };
}

/**
 * Pane C — who you're talking to, what you've shared, and every per-chat
 * control. Docked on xl screens, an overlay drawer below that.
 */
export default function DetailsPane({
  active,
  messages = [],
  primary,
  online,
  isFavorite,
  isMuted,
  wallpaper,
  onWallpaper,
  onToggleFavorite,
  onToggleMute,
  onBlock,
  onReport,
  onDeleteChat,
  onSearchInChat,
  onOpenImage,
  onClose,
  docked,
}) {
  const [tab, setTab] = useState("media");
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState(REPORT_REASONS[0]);
  const [wallOpen, setWallOpen] = useState(false);

  // Classify on the attachment's URL part, not the raw body — a captioned photo
  // is stored as "<url>\n<caption>", so testing the whole body would miss the
  // image and wrongly bucket it as a link.
  const media = useMemo(
    () =>
      messages
        .filter((m) => !m.deleted && attachmentType(m.body) === "image")
        .map((m) => attachmentParts(m.body).url)
        .reverse(),
    [messages]
  );
  const files = useMemo(
    () =>
      messages
        .filter((m) => {
          if (m.deleted) return false;
          const t = attachmentType(m.body);
          return t === "file" || t === "audio" || t === "video";
        })
        .map((m) => ({ id: m.id, url: attachmentParts(m.body).url, created_at: m.created_at }))
        .reverse(),
    [messages]
  );
  const links = useMemo(
    () =>
      messages
        .filter((m) => !m.deleted && attachmentType(m.body) === null && /https?:\/\//i.test(m.body || ""))
        .reverse(),
    [messages]
  );

  const Action = ({ icon: Icon, label, sub, onClick, danger, active: on }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
        danger ? "text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/15" : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      <span className={`shrink-0 ${danger ? "" : on ? "" : "text-slate-400"}`} style={on && !danger ? { color: primary } : undefined}>
        <Icon className="h-[18px] w-[18px]" filled={on} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold">{label}</span>
        {sub && <span className="block truncate text-[11px] text-slate-400">{sub}</span>}
      </span>
    </button>
  );

  const tabs = [
    ["media", "Media", media.length],
    ["files", "Files", files.length],
    ["links", "Links", links.length],
  ];

  return (
    <aside
      className={`flex h-full w-full flex-col bg-surface ${docked ? "border-l border-slate-100" : ""}`}
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h3 className="text-[13px] font-extrabold uppercase tracking-wide text-slate-400">Chat details</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close details"
          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="thin-scroll min-h-0 flex-1 overflow-y-auto">
        {/* profile card */}
        <div className="flex flex-col items-center gap-2 px-5 pb-4 pt-6 text-center">
          <Avatar name={active?.name} img={active?.img} primary={primary} size={84} gradientRing online={online} />
          <p className="mt-1 text-[17px] font-extrabold tracking-tight text-slate-900">{active?.name}</p>
          <p className="text-[11px] font-semibold text-slate-400">
            {online ? <span className="text-emerald-500">Active now</span> : "Member of this membership"}
          </p>
          {(active?.points != null || active?.level != null) && (
            <div className="mt-2 flex items-center gap-2">
              {active?.level != null && (
                <span className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold" style={{ backgroundColor: `${primary}14`, color: primary }}>
                  <SparkIcon className="h-3 w-3" /> Level {active.level}
                </span>
              )}
              {active?.points != null && (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500">{active.points} pts</span>
              )}
            </div>
          )}
        </div>

        {/* shared content */}
        <div className="px-3">
          <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-0.5">
            {tabs.map(([key, label, n]) => {
              const on = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-bold transition ${
                    on ? "bg-surface shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                  style={on ? { color: primary } : undefined}
                >
                  {label}
                  <span className={`text-[10px] ${on ? "opacity-70" : "text-slate-400"}`}>{n}</span>
                </button>
              );
            })}
          </div>

          <div className="py-3">
            {tab === "media" &&
              (media.length === 0 ? (
                <p className="py-6 text-center text-[11px] font-medium text-slate-400">No photos shared yet.</p>
              ) : (
                <div className="grid grid-cols-3 gap-1.5">
                  {media.slice(0, 18).map((url, i) => (
                    <button
                      key={`${url}-${i}`}
                      type="button"
                      onClick={() => onOpenImage(url)}
                      className="aspect-square overflow-hidden rounded-lg ring-1 ring-slate-200/70 transition hover:opacity-85"
                    >
                      <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              ))}

            {tab === "files" &&
              (files.length === 0 ? (
                <p className="py-6 text-center text-[11px] font-medium text-slate-400">No files shared yet.</p>
              ) : (
                <ul className="space-y-1">
                  {files.slice(0, 20).map((m) => (
                    <li key={m.id}>
                      <a href={m.url} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 rounded-xl px-2 py-2 transition hover:bg-slate-50">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                          <DocIcon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12px] font-semibold text-slate-700">{fileNameFromUrl(m.url)}</span>
                          <span className="block text-[10px] text-slate-400">{shortTime(m.created_at)}</span>
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              ))}

            {tab === "links" &&
              (links.length === 0 ? (
                <p className="py-6 text-center text-[11px] font-medium text-slate-400">No links shared yet.</p>
              ) : (
                <ul className="space-y-1">
                  {links.slice(0, 20).map((m) => (
                    <li key={m.id} className="rounded-xl px-2 py-2 transition hover:bg-slate-50">
                      <span className="block truncate text-[12px] font-medium text-slate-600">{m.body}</span>
                      <span className="block text-[10px] text-slate-400">{shortTime(m.created_at)}</span>
                    </li>
                  ))}
                </ul>
              ))}
          </div>
        </div>

        {/* actions */}
        <div className="border-t border-slate-100 p-2">
          <Action icon={StarIcon} label={isFavorite ? "Remove from favourites" : "Add to favourites"} onClick={onToggleFavorite} active={isFavorite} />
          <Action icon={isMuted ? BellIcon : BellOffIcon} label={isMuted ? "Unmute notifications" : "Mute notifications"} onClick={onToggleMute} />
          <Action icon={SearchIcon} label="Search in chat" onClick={onSearchInChat} />
          <Action icon={PaletteIcon} label="Chat wallpaper" sub={WALLPAPERS.find((w) => w.key === wallpaper)?.label} onClick={() => setWallOpen((o) => !o)} />
          {wallOpen && (
            <div className="chat-pop grid grid-cols-3 gap-2 px-3 py-2">
              {WALLPAPERS.map((w) => (
                <button
                  key={w.key}
                  type="button"
                  onClick={() => {
                    onWallpaper(w.key);
                    setWallOpen(false);
                  }}
                  className={`flex h-12 items-end justify-center rounded-lg p-1 text-[9px] font-bold ring-1 transition ${
                    wallpaper === w.key ? "ring-2" : "ring-slate-200 hover:ring-slate-300"
                  }`}
                  style={{ ...wallpaperStyle(w.key, primary), ...(wallpaper === w.key ? { "--tw-ring-color": primary } : {}) }}
                >
                  <span className="rounded bg-surface/80 px-1 text-slate-600">{w.label}</span>
                </button>
              ))}
            </div>
          )}
          <div className="my-1 h-px bg-slate-100" />
          <Action icon={FlagIcon} label="Report member" onClick={() => setReportOpen(true)} danger />
          <Action icon={BanIcon} label="Block member" onClick={onBlock} danger />
          <Action icon={TrashIcon} label="Delete chat" onClick={onDeleteChat} danger />
        </div>
      </div>

      {/* report sheet */}
      {reportOpen && (
        <div className="absolute inset-0 z-30 flex items-end bg-ink-slate/40 backdrop-blur-[2px] sm:items-center sm:justify-center" onClick={() => setReportOpen(false)}>
          <div className="chat-pop w-full rounded-t-2xl bg-surface p-4 shadow-2xl sm:m-4 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-50 dark:bg-rose-500/15 text-rose-500">
                <FlagIcon className="h-[18px] w-[18px]" />
              </span>
              <div>
                <p className="text-sm font-extrabold text-slate-900">Report {active?.name}</p>
                <p className="text-[11px] text-slate-400">Only admins can see this report.</p>
              </div>
            </div>
            <div className="space-y-1">
              {REPORT_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(r)}
                  className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13px] font-medium transition ${
                    reason === r ? "text-slate-900" : "text-slate-600 hover:bg-slate-50"
                  }`}
                  style={reason === r ? { backgroundColor: `${primary}12` } : undefined}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${reason === r ? "border-transparent" : "border-slate-300"}`}
                    style={reason === r ? { backgroundColor: primary } : undefined}
                  >
                    {reason === r && <span className="h-1.5 w-1.5 rounded-full bg-surface" />}
                  </span>
                  {r}
                </button>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setReportOpen(false)} className="flex-1 rounded-xl border border-slate-200 py-2.5 text-[13px] font-bold text-slate-600 transition hover:bg-slate-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setReportOpen(false);
                  onReport(reason);
                }}
                className="flex-1 rounded-xl py-2.5 text-[13px] font-bold text-white shadow-sm transition hover:brightness-110"
                style={brandGradient(primary)}
              >
                Submit report
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
